import { Link } from '@tanstack/react-router'
import { Menu, MenuGroup, MenuRow, menuItem } from './Menu'

/**
 * The areas of the Admin Site, as a row in the masthead. Every area is about the one Exam
 * in the address, so there is no row to show until an Exam has been chosen.
 *
 * The treatment is the static sites' section nav (OutscoreStaticSite Navbar.astro, the row
 * holding Pricing): bold, at the nav type size, over a 2px bottom border that is transparent
 * until the area is the one you are in. Two cues rather than colour alone, because hover already
 * moves an ordinary link toward green and WCAG 1.4.1 will not have colour carrying a distinction
 * by itself. Every item keeps the border and the padding at all times, so nothing shifts by 2px
 * as you move along the row.
 *
 * Centred between the mark and the icons, as the static sites centre their section links: the
 * row keeps its balance as areas are added, rather than growing rightwards into the icons.
 *
 * An area is a menu rather than a link, and does NOT navigate: a click opens the panel. If one
 * ever gains a landing page it earns a row inside the panel, not a second behaviour on the
 * trigger -- one nav item with two targets is the thing that makes these menus fiddly on touch.
 *
 * Sub-pages sit in ONE panel under headings. See Menu.tsx for why it is not a flyout inside
 * a flyout.
 */
export function AreaNav({ exam, unread = 0 }: { exam: string | null; unread?: number }) {
  if (!exam) return null

  const badge = unread > 0 ? <Unread count={unread} /> : undefined

  return (
    <nav aria-label="Areas" className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2">
      <Menu label="Exam" text align="centre">
        <MenuRow>
          <Link to="/$exam/sittings" params={{ exam }} className={menuItem}>
            Sittings
          </Link>
        </MenuRow>
        <MenuRow>
          <Link to="/$exam/topics" params={{ exam }} className={menuItem}>
            Topics
          </Link>
        </MenuRow>
      </Menu>
      {/* Two columns, so what an Admin does to the Questions and what they do to a YAML file
          are two short lists to read across rather than one long one to read down. Both are
          headed now: side by side, an unheaded column beside a headed one reads as the heading
          having slipped. Still one panel, not a flyout inside a flyout (Menu.tsx). */}
      <Menu label="Questions" text align="centre" wide badge={badge}>
        <MenuGroup label="Bank">
          <MenuRow>
            <Link to="/$exam/questions" params={{ exam }} className={menuItem}>
              <span className="relative">
                Manage
                {badge}
              </span>
            </Link>
          </MenuRow>
          <MenuRow>
            <Link to="/$exam/mocks" params={{ exam }} className={menuItem}>Mocks</Link>
          </MenuRow>
          <MenuRow>
            <Link to="/$exam/questions/practice" params={{ exam }} className={menuItem}>Practice</Link>
          </MenuRow>
          <MenuRow>
            <Link to="/$exam/questions/coverage" params={{ exam }} className={menuItem}>
              Coverage
            </Link>
          </MenuRow>
        </MenuGroup>
        <MenuGroup label="Yaml">
          <MenuRow>
            <Link to="/$exam/questions/yaml-preview" params={{ exam }} className={menuItem}>
              Preview
            </Link>
          </MenuRow>
          <MenuRow>
            <Link to="/$exam/questions/schema" params={{ exam }} className={menuItem}>
              Schema
            </Link>
          </MenuRow>
        </MenuGroup>
      </Menu>
      <Menu label="Support" text align="centre">
        <MenuRow>
          <Link to="/$exam/support" params={{ exam }} className={menuItem}>
            Students
          </Link>
        </MenuRow>
      </Menu>
    </nav>
  )
}

/**
 * Q Feedback nobody has read, riding on the words that lead to it: the App's own count on
 * Practice, copied rather than shared, since the two SPAs share tokens and not components.
 * The pill is decoration; the sentence beside it is what a screen reader says, and it is
 * in the name rather than a tooltip because a tooltip is not where a count's meaning lives.
 */
function Unread({ count }: { count: number }) {
  const two = String(count).padStart(2, '0')

  return (
    <>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -top-[5px] -right-[22px] inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-green-800 p-[3px] text-[12px] leading-none font-bold tabular-nums text-sand-050"
      >
        {two}
      </span>
      <span className="sr-only">, {two} unread Q Feedback</span>
    </>
  )
}
